import React, { useEffect, useMemo, useState } from "react";

import { getTpmSummaryReport } from "../../../../services/features/reports.service";
import {
  KpiRow,
  KpiTile,
  ReportGrid,
  ChartCard,
  EmptyNote,
  ReportBar,
  ReportDonut,
  SERIES,
  BRAND,
  OK_COLOR,
  num,
  barData,
  barOptions,
  donutData,
  donutOptions,
  allZero,
} from "./reportShared";

// Report 3 — Reporting & Data Quality. Health of the reporting pipeline: how
// many visit reports are being written, submitted and approved, and how many
// field-team / IP-donor reports are completed.
const ReportingQualityReport = ({ onReady, onLoadingChange }) => {
  const projectId = localStorage.getItem("currentProjectID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    getTpmSummaryReport(
      { projectId },
      (res) => {
        setData(res?.data || null);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectId]);

  const svt = data?.siteVisitTracking || {};
  const ipd = data?.ipDonorReporting || {};

  const writing = num(svt.reportWriting);
  const submitted = num(svt.reportSubmitted);
  const approved = num(svt.reportApproved);
  const fieldCompleted = num(ipd.completedFieldTeamReporting);
  const ipDonorCompleted = num(ipd.completedIPDonorReports);
  const fieldOther = num(ipd.otherStatusesFieldTeamReporting);
  const fieldAwaiting = num(ipd.awaitingFieldReportings);

  const kpis = useMemo(
    () => [
      { label: "In writing", value: writing, color: SERIES[0] },
      { label: "Submitted", value: submitted, color: SERIES[1] },
      { label: "Approved", value: approved, color: OK_COLOR },
      { label: "Field-team reports done", value: fieldCompleted, color: SERIES[3] },
      { label: "IP/Donor reports done", value: ipDonorCompleted, color: BRAND },
    ],
    [writing, submitted, approved, fieldCompleted, ipDonorCompleted]
  );

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  useEffect(() => {
    if (!loading) onReady?.(kpis);
  }, [loading, kpis, onReady]);

  if (loading) return null;

  const pipelineRows = [
    { label: "Writing", value: writing, color: SERIES[0] },
    { label: "Submitted", value: submitted, color: SERIES[1] },
    { label: "Approved", value: approved, color: OK_COLOR },
  ];
  const fieldRows = [
    { label: "Completed", value: fieldCompleted, color: OK_COLOR },
    { label: "Other statuses", value: fieldOther, color: SERIES[0] },
    { label: "Awaiting", value: fieldAwaiting, color: SERIES[6] },
  ];

  return (
    <>
      <KpiRow>
        {kpis.map((k) => (
          <KpiTile key={k.label} {...k} />
        ))}
      </KpiRow>

      <ReportGrid>
        <ChartCard title="Report approval pipeline" hint="Writing → approved">
          {allZero(pipelineRows) ? (
            <EmptyNote />
          ) : (
            <ReportBar data={barData(pipelineRows, SERIES)} options={barOptions(false)} />
          )}
        </ChartCard>

        <ChartCard title="Field-team reporting status">
          {allZero(fieldRows) ? (
            <EmptyNote />
          ) : (
            <ReportDonut data={donutData(fieldRows)} options={donutOptions} />
          )}
        </ChartCard>

        <ChartCard title="IP/Donor reports completed">
          <div className="reportBigStat">
            <span className="reportBigStatValue" style={{ color: BRAND }}>
              {ipDonorCompleted}
            </span>
            <span className="reportBigStatUnit">reports</span>
            <span className="reportBigStatCaption">
              IP/Donor reports marked completed
            </span>
          </div>
        </ChartCard>
      </ReportGrid>
    </>
  );
};

export default ReportingQualityReport;
